/** Incident reports: the numbers that matter across the top, work queues as tabs, and every report in one table. */
import { useEffect, useState } from 'react';
import { OFFENSE_BY_CODE } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { fmtStamp } from '../shared/format.ts';
import { Icon } from '../shared/icons.tsx';
import { useRouter } from '../shared/router.tsx';
import { Badge, Empty, ErrorBox, Flag, PageHeader, Spinner, useAction, useLoad, useNarrow } from '../shared/ui.tsx';
import { reportsBase } from './ReportWizard.tsx';
import { lastFirst, offenseShort, officerShort as officer } from './names.ts';

type Tab = 'mine' | 'review' | 'returned' | 'approved' | 'all';
const STATUS: Record<string, [string, 'neutral' | 'info' | 'warn' | 'ok']> = {
  draft: ['Draft', 'neutral'],
  submitted: ['Awaiting approval', 'info'],
  returned: ['Returned', 'warn'],
  approved: ['Approved', 'ok'],
};
const inField = () => location.pathname.startsWith('/field');
export function ReportsPage() {
  const { can, me } = useAuth();
  const { navigate } = useRouter();
  const narrow = useNarrow();
  const approver = can('reports.approve' as any);
  const reader = can('reports.read' as any) || approver;
  const writer = can('reports.write' as any) || approver;
  const [tab, setTab] = useState<Tab>(() => (approver ? 'review' : 'mine'));
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const effectiveTab: Tab = query && reader ? 'all' : tab;
  const r = useLoad(() => api.get<any>(`/api/reports?tab=${effectiveTab}${query ? `&q=${encodeURIComponent(query)}` : ''}`), [effectiveTab, query]);
  const sups = useLoad(() => (writer ? api.get<any>('/api/supplements') : Promise.resolve({ supplements: [] })), [effectiveTab, query]);
  const supsHere = (sups.data?.supplements ?? []).filter((x: any) =>
    tab === 'review' ? x.status === 'submitted' && x.author_id !== me?.user.id : tab === 'mine' ? x.author_id === me?.user.id : tab === 'returned' ? x.status === 'returned' && x.author_id === me?.user.id : false,
  );
  const a = useAction();
  const start = (template?: 'arrest') =>
    a.run(async () => {
      const x = await api.post<{ id: string }>('/api/reports', template ? { template } : {});
      navigate(`${reportsBase()}/${x.id}${template === 'arrest' ? '?step=people' : ''}`);
    });
  // Quick jump (Ctrl+K) can open this page with ?new=incident or ?new=arrest to start one right away.
  const { path } = useRouter();
  useEffect(() => {
    const n = new URLSearchParams(path.split('?')[1] ?? '').get('new');
    if (writer && (n === 'incident' || n === 'arrest')) start(n === 'arrest' ? 'arrest' : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
  const counts = r.data?.counts ?? { mine_open: 0, review: 0, returned: 0 };
  const stats = r.data?.stats;
  const tabs: [Tab, string, number | null, boolean][] = [
    ['review', 'Awaiting my review', counts.review || null, approver],
    ['mine', 'My reports', counts.mine_open || null, writer],
    ['returned', 'Returned to me', counts.returned || null, writer],
    ['approved', 'Approved', null, true],
    ['all', 'All reports', null, reader],
  ];
  const open = (id: string) => navigate(`${reportsBase()}/${id}`);
  const rows: any[] = r.data?.reports ?? [];
  const field = inField();
  // A deputy's own evidence: what they collected that hasn't been released or destroyed.
  const ev = useLoad(() => (field ? api.get<any>('/api/evidence?tab=mine') : Promise.resolve({ items: [] })), []);
  const myEv: any[] = (ev.data?.items ?? []).filter((x: any) => x.status !== 'released' && x.status !== 'destroyed');
  const myEvWaiting = myEv.filter((x) => x.status === 'pending').length;
  const page = (
    <div className="stack rp">
      <PageHeader
        crumb="Records"
        title="Incident reports"
        actions={
          <>
            {writer && !field && (
              <button className="btn" onClick={() => start('arrest')} disabled={a.busy}>
                + Arrest report
              </button>
            )}
            {writer && !field && (
              <button className="btn primary" onClick={() => start()} disabled={a.busy}>
                + New incident
              </button>
            )}
          </>
        }
      />
      <ErrorBox error={a.error} />
      {stats && (
        <div className="rp-kpis">
          <div className="rp-kpi">
            <div className="l">Open cases</div>
            <div className="n">{stats.open_cases}</div>
            <div className="d">Approved, not cleared</div>
          </div>
          <div className={`rp-kpi${stats.awaiting ? ' hot' : ''}`}>
            <div className="l">Awaiting approval</div>
            <div className="n">{stats.awaiting}</div>
            <div className="d">Submitted reports</div>
          </div>
          <div className="rp-kpi">
            <div className="l">Crimes, last 30 days</div>
            <div className="n">{stats.crimes_30}</div>
            <div className="d">Approved reports</div>
          </div>
          <div className="rp-kpi">
            <div className="l">Clearance, last 30 days</div>
            <div className="n">{stats.crimes_30 ? `${Math.round((stats.cleared_30 / stats.crimes_30) * 100)}%` : '—'}</div>
            <div className="d">
              {stats.cleared_30} of {stats.crimes_30} cleared
            </div>
          </div>
          <div className="rp-kpi">
            <div className="l">Arrests, last 30 days</div>
            <div className="n">{stats.arrests_30}</div>
            <div className="d">On reports</div>
          </div>
          {field && (
            <button type="button" className={`rp-kpi rp-kpi-btn${myEvWaiting ? ' hot' : ''}`} onClick={() => navigate('/field/evidence?tab=mine')}>
              <div className="l">My evidence</div>
              <div className="n">{ev.data ? myEv.length : '–'}</div>
              <div className="d">{myEvWaiting ? `${myEvWaiting} not checked in yet` : 'Items you collected'}</div>
            </button>
          )}
        </div>
      )}
      <div className="filterbar">
        <input className="rp-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search case #, name, address, tag, serial #, narrative…" aria-label="Search reports" />
        {query && <span className="muted small">Searching all reports</span>}
        <span className="spacer" />
        {r.data && <span className="muted small">{rows.length === 200 ? 'First 200' : `${rows.length} report${rows.length === 1 ? '' : 's'}`}</span>}
      </div>
      {!query && (
        <div className="tabs" role="tablist">
          {tabs
            .filter((t) => t[3])
            .map(([k, label, n]) => (
              <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
                {label} {n ? <span className="rp-n">{n}</span> : null}
              </button>
            ))}
        </div>
      )}
      {!query && supsHere.length > 0 && (
        <section className="card flush">
          <header className="card-h" style={{ margin: 0 }}>
            <h2>Supplements</h2>
          </header>
          <div className="table-wrap">
            <table className="table rp-table">
              <thead>
                <tr>
                  <th>Case #</th>
                  <th>Supplement</th>
                  <th>Officer</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {supsHere.map((x: any) => {
                  const [label, tone] = STATUS[x.status] ?? [x.status, 'neutral'];
                  return (
                    <tr key={x.id} className="clickable" onClick={() => navigate(`${reportsBase()}/supplements/${x.id}`)}>
                      <td className="mono">{x.case_number}</td>
                      <td className="wrap">
                        <button type="button" className="btn link" style={{ padding: 0 }} onClick={(e) => (e.stopPropagation(), navigate(`${reportsBase()}/supplements/${x.id}`))}>
                          Supplement {x.number} · {x.title}
                        </button>
                        {x.status === 'returned' && x.return_note && <span className="rp-note">“{x.return_note}”</span>}
                      </td>
                      <td>{x.author_name}</td>
                      <td>
                        <Badge tone={tone} dot>
                          {label}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Empty>{query ? 'No reports match.' : tab === 'review' ? 'Nothing waiting for your review.' : tab === 'mine' ? 'No reports yet. Use “New incident” to start one.' : 'None.'}</Empty>
      ) : (
        <>
          {!narrow && (<div className="card flush rp-table-wrap">
            <div className="table-wrap">
              <table className="table rp-table rp-fixed">
                <colgroup>
                  <col style={{ width: 116 }} />
                  <col style={{ width: 118 }} />
                  <col style={{ width: '21%' }} />
                  <col />
                  <col style={{ width: '14%' }} />
                  <col className="rp-c-off" style={{ width: 98 }} />
                  <col style={{ width: 140 }} />
                  <col className="rp-c-flags" style={{ width: 146 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Case #</th>
                    <th>Occurred</th>
                    <th>Offense</th>
                    <th>Location</th>
                    <th>Victim / subject</th>
                    <th className="rp-c-off">Officer</th>
                    <th>Status</th>
                    <th className="rp-c-flags">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((x) => {
                    const [label, tone] = STATUS[x.status] ?? [x.status, 'neutral'];
                    const n = x.codes?.length ?? 0;
                    const def = x.primary_code ? OFFENSE_BY_CODE.get(x.primary_code) : undefined;
                    const flags: [string, 'bad' | 'warn' | 'info' | undefined][] = [];
                    if (x.arrests > 0) flags.push([x.arrests > 1 ? `${x.arrests} arrests` : 'Arrest', 'bad']);
                    if (x.stolen_vehicle) flags.push(['Stolen veh', 'bad']);
                    if (x.firearm) flags.push(['Firearm', 'warn']);
                    if (x.domestic) flags.push(['DV', 'warn']);
                    if (x.evidence) flags.push(['Evidence', 'info']);
                    const where = x.location_text as string | null;
                    return (
                      <tr key={x.id} className="clickable" onClick={() => open(x.id)}>
                        <td className="mono">{x.case_number}</td>
                        <td className="nowrap">{x.occurred_at ? fmtStamp(x.occurred_at) : <span className="muted">—</span>}</td>
                        <td className="ell">
                          <span className="rp-off">
                            {x.primary_code && def?.group !== 'NC' && <span className="code-chip">{x.primary_code}</span>}
                            <button type="button" className="btn link rp-open" title={def?.name} onClick={(e) => (e.stopPropagation(), open(x.id))}>
                              {offenseShort(def, x.title || 'New incident (nothing entered yet)')}
                            </button>
                            {n > 1 && <span className="rp-more">+{n - 1}</span>}
                          </span>
                          {x.status === 'returned' && x.return_note && <span className="rp-note">“{x.return_note}”</span>}
                        </td>
                        <td className="ell" title={where ?? undefined}>{where || <span className="muted">—</span>}</td>
                        <td className="ell">{lastFirst(x.victim || x.subject) || <span className="muted">—</span>}</td>
                        <td className="ell rp-c-off" title={x.author_name}>{officer(x.author_name)}</td>
                        <td className="nowrap" title={x.status === 'submitted' && x.reviewer_name ? `Sent to ${x.reviewer_name}` : undefined}>
                          <Badge tone={tone} dot>
                            {label}
                          </Badge>
                        </td>
                        <td className="rp-c-flags">
                          <span className="rp-flags" title={flags.map((f) => f[0]).concat(x.cleared ? ['Cleared'] : []).join(', ') || undefined}>
                            {flags.slice(0, flags.length > 2 ? 1 : 2).map(([t, tone]) => (
                              <Flag key={t} tone={tone}>
                                {t}
                              </Flag>
                            ))}
                            {flags.length > 2 && <span className="rp-more">+{flags.length - 1}</span>}
                            {!flags.length && x.cleared && <Flag>Cleared</Flag>}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
)}
          {/* Phone: one card per report. */}
          {narrow && (<ul className="rp-list rp-cards">
            {rows.map((x) => {
              const [label, tone] = STATUS[x.status] ?? [x.status, 'neutral'];
              return (
                <li key={x.id}>
                  <button onClick={() => open(x.id)}>
                    <div className="rp-main">
                      <strong>{offenseShort(x.primary_code ? OFFENSE_BY_CODE.get(x.primary_code) : undefined, x.title || 'New incident (nothing entered yet)')}</strong>
                      <span className="muted small">
                        <span className="mono">{x.case_number}</span> · {x.location_text || 'no address yet'} · {x.occurred_at ? fmtStamp(x.occurred_at) : 'no date yet'}
                      </span>
                      {x.status === 'returned' && x.return_note && <span className="small rp-note">“{x.return_note}”</span>}
                    </div>
                    <div className="rp-side">
                      <Badge tone={tone} dot>
                        {label}
                      </Badge>
                      {x.arrests > 0 && <Flag tone="bad">Arrest</Flag>}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>)}
        </>
      )}
    </div>
  );
  if (!field) return page;
  return (
    <div className="rp-shell">
      <aside className="rp-rail" aria-label="Records menu">
        {writer && (
          <div className="rp-rail-start">
            <button type="button" className="rp-rail-new" onClick={() => start()} disabled={a.busy}>
              <span className="rp-rail-plus" aria-hidden>
                +
              </span>
              <span>
                <strong>New incident</strong>
                <small>Start a report</small>
              </span>
            </button>
            <button type="button" className="rp-rail-sub" onClick={() => start('arrest')} disabled={a.busy}>
              <Icon name="arrest" size={16} /> Arrest report
            </button>
          </div>
        )}
        <nav className="rp-rail-nav">
          <div className="rp-rail-h">Records</div>
          <button type="button" className="on" onClick={() => setTab(writer ? 'mine' : 'all')}>
            <Icon name="report" size={18} />
            <span>Incident reports</span>
            {counts.mine_open ? <span className="rp-rail-n">{counts.mine_open}</span> : null}
          </button>
          <button type="button" onClick={() => navigate('/field/evidence?tab=mine')}>
            <Icon name="evidence" size={18} />
            <span>My evidence</span>
            {myEv.length ? <span className={`rp-rail-n${myEvWaiting ? ' hot' : ''}`}>{myEv.length}</span> : null}
          </button>
          <button type="button" onClick={() => navigate('/field/evidence')}>
            <Icon name="layers" size={18} />
            <span>Evidence room</span>
          </button>
          <button type="button" onClick={() => navigate('/field/arrests')}>
            <Icon name="people" size={18} />
            <span>Recent arrests</span>
          </button>
          {reader && (
            <button type="button" onClick={() => navigate('/field/reports/analytics')}>
              <Icon name="chart" size={18} />
              <span>Crime analytics</span>
            </button>
          )}
          {reader && (
            <button type="button" onClick={() => navigate(`${reportsBase()}/similar`)}>
              <Icon name="search" size={18} />
              <span>Similar cases</span>
            </button>
          )}
          {writer && (
            <>
              <button type="button" onClick={() => navigate('/field/tickets')}>
                <Icon name="pen" size={18} />
                <span>E-tickets</span>
              </button>
              <button type="button" onClick={() => navigate('/field/tows')}>
                <Icon name="car" size={18} />
                <span>Tow reports</span>
              </button>
            </>
          )}
          <div className="rp-rail-h">Coming soon</div>
          <button type="button" disabled title="Coming soon">
            <Icon name="phone" size={18} />
            <span>Calls for service</span>
            <span className="rp-rail-soon">Soon</span>
          </button>
        </nav>
      </aside>
      {page}
    </div>
  );
}
